import Services from './Services';

const host = process.env.REACT_APP_HOST;
const env = process.env.NODE_ENV;
const isProduction = env === 'production';

const baseUrl = isProduction ? `https://${host}` : `http://${host}`;

// HTTPS location
const Locations = {
  [Services.ProfitCalculations]: 'profit-calcs',
  [Services.Data]: 'data',
  [Services.CandleFetcher]: 'candle-fetcher',
  [Services.Control]: 'control',
  [Services.Order]: 'order',
  [Services.Exchange]: 'exchange',
};

const Ports = {
  [Services.ProfitCalculations]: 5000,
  [Services.Data]: 5010,
  [Services.CandleFetcher]: 5020,
  [Services.Control]: 5030,
  [Services.Order]: 5040,
  [Services.Exchange]: 5050,
};

const WebsocketPorts = {
  [Services.ProfitCalculations]: 5001,
  [Services.Data]: 5011,
  [Services.CandleFetcher]: 5021,
  [Services.Control]: 5031,
  [Services.Order]: 5041,
  [Services.Exchange]: 5051,
};

const basePaths = {
  [Services.Bot]: `${baseUrl}`,
  [Services.ProfitCalculations]: isProduction
    ? `${baseUrl}/${Locations[Services.ProfitCalculations]}`
    : `${baseUrl}:${Ports[Services.ProfitCalculations]}`,
  [Services.Data]: isProduction
    ? `${baseUrl}/${Locations[Services.Data]}`
    : `${baseUrl}:${Ports[Services.Data]}`,
  [Services.CandleFetcher]: isProduction
    ? `${baseUrl}/${Locations[Services.CandleFetcher]}`
    : `${baseUrl}:${Ports[Services.CandleFetcher]}`,
  [Services.Control]: isProduction
    ? `${baseUrl}/${Locations[Services.Control]}`
    : `${baseUrl}:${Ports[Services.Control]}`,
  [Services.Order]: isProduction
    ? `${baseUrl}/${Locations[Services.Order]}`
    : `${baseUrl}:${Ports[Services.Order]}`,
  [Services.Exchange]: isProduction
    ? `${baseUrl}/${Locations[Services.Exchange]}`
    : `${baseUrl}:${Ports[Services.Exchange]}`,
};

// In production the page is served over HTTPS, so a browser refuses to open an
// insecure `ws://` socket (Mixed Content -> SecurityError, blocked before any
// handshake). The websocket must therefore be `wss://` and ride the same 443
// reverse proxy the HTTPS API calls use. We route each service's websocket
// through a dedicated `/ws/<location>` path which nginx proxy_pass-es to that
// service's websocket port (its HTTP port + 1) with the Upgrade/Connection
// headers. In dev the page is plain HTTP, so we hit the websocket port directly.
const wsBaseUrl = isProduction ? `wss://${host}` : `ws://${host}`;

const websocketPaths = {
  [Services.Bot]: wsBaseUrl,
  [Services.ProfitCalculations]: isProduction
    ? `${wsBaseUrl}/ws/${Locations[Services.ProfitCalculations]}`
    : `${wsBaseUrl}:${WebsocketPorts[Services.ProfitCalculations]}`,
  [Services.Data]: isProduction
    ? `${wsBaseUrl}/ws/${Locations[Services.Data]}`
    : `${wsBaseUrl}:${WebsocketPorts[Services.Data]}`,
  [Services.CandleFetcher]: isProduction
    ? `${wsBaseUrl}/ws/${Locations[Services.CandleFetcher]}`
    : `${wsBaseUrl}:${WebsocketPorts[Services.CandleFetcher]}`,
  [Services.Control]: isProduction
    ? `${wsBaseUrl}/ws/${Locations[Services.Control]}`
    : `${wsBaseUrl}:${WebsocketPorts[Services.Control]}`,
  [Services.Order]: isProduction
    ? `${wsBaseUrl}/ws/${Locations[Services.Order]}`
    : `${wsBaseUrl}:${WebsocketPorts[Services.Order]}`,
  [Services.Exchange]: isProduction
    ? `${wsBaseUrl}/ws/${Locations[Services.Exchange]}`
    : `${wsBaseUrl}:${WebsocketPorts[Services.Exchange]}`,
};

export type ServicePaths = {
  BOT: {
    Health: (port: number) => string;
    StartLogsWebsocket: (port: number) => string;
    StopLogsWebsocket: (port: number) => string;
    Websocket: (port: number) => string;

    GetBot: (port: number) => string;
    CreateComplexParams: (port: number) => string;
    UpdateComplexParams: (port: number) => string;
    PushComplexParams: (port: number) => string;
  };
  DATA: {
    Health: string;
    StartLogsWebsocket: string;
    StopLogsWebsocket: string;
    Websocket: string;

    GetUser: string;
    AddUser: string;
    EditUser: string;
    RemoveUser: string;
    GetUserEnabledExchanges: string;
    AddUserEnabledExchange: string;
    RemoveUserEnabledExchange: string;
    UpdateUserTotalBalances: string;
    GetAllBotsByUserId: string;
    GetClustersByUserId: string;

    GetBotIdsByClusterId: string;

    AddBot: string;
    EditBot: string;
    GetAllBots: string;
    GetAllActiveBots: string;
    IsPortAvailable: string;
    GetBot: string;
    SetBotTimeFrames: string;
    SetBotProcessID: string;
    SetBotComplexParams: string;
    SetBotIndicatorConfig: string;
    SetDidEarlierOrder: string;
    ResetRuntime: string;
    SetCbrtRange: string;
    SetLatestOrder: string;
    ResetCbrtRange: string;
    DisableBot: string;
    StartBot: string;
    StopBot: string;
    SetRetryUpdateComplexParams: string;
    SetLastUpdated: string;
    SetNextWakeUpState: string;
    SetActivePort: string;
    SetProfitCalculationsStartTimestamp: string;
    GetBotByBaseAsset: string;
    UpdateBalance: string;
    RemoveBot: string;
    GetBotAvailableExchangesByBotFriendlyName: string;
    SetBotDefaultExchange: string;
    AddBotAvailableExchanges: string;
    RemoveBotAvailableExchanges: string;
    ResetBotAvailableExchanges: string;
    ResetAllBotsAvailableExchanges: string;
    ResetMultiOrder: string;
    FetchSubscriptionsByBotId: string;
    SetMultiOrder: string;
    SetLatestTransactionType: string;

    AddSubscription: string;
    RemoveSubscription: string;
    GetSubscriptionByUserIdAndBotIdUrlClusterId: string;
    SubscriptionBalance: string;
    GetSubscriptionsByUserId: string;
    SubscriptionExchange: string;
    SetSubscriptionRuntimeVolume: string;
    ResetSubscriptionRuntimeVolume: string;

    AddTransaction: string;
    AddCustomTransaction: string;
    GetTransactionSize: string;
    GetCustomTransactionSize: string;

    GetClientHealthCheck: string;
    GetClientOverview: string;
    GetClientSubscription: string;
    GetClientNextWakeUpInfo: string;
    FetchClientNextOrderValue: string;
    GetClientBotProfitCharts: string;
    GetClientBotProfits: string;
    GetClientBotProfitsByPeriod: string;
    GetClientBotTransactions: string;
    GetClientLatestTransaction: string;
    GetClientBotWakeups: string;
    GetClientLatestBotState: string;

    ReorderDaemonQueue: string;
  };
  CANDLE_FETCHER: {
    Health: string;
    StartLogsWebsocket: string;
    StopLogsWebsocket: string;
    Websocket: string;

    CheckCompleteStorage: string;
    FetchCandles: string;
    FetchHeikinCandles: string;
    FetchPreviousHeikinCandle: string;
    FetchLatestHeikinCandle: string;
    FetchCurrentHeikinCandle: string;
    FetchLatestNormalCandle: string;
    FetchCurrentNormalCandle: string;
    StartFetchInterval: string;
    StopFetchInterval: string;
    SetCandleDecision: string;
  };
  CONTROL: {
    Health: string;
    StartLogsWebsocket: string;
    StopLogsWebsocket: string;
    Websocket: string;

    GetUser: string;
    EditUser: string;
    AddUser: string;
    RemoveUser: string;
    HasPassword: string;
    SetPassword: string;
    RemovePassword: string;
    PanicSell: string;

    GetRawBot: string;
    AddBot: string;
    EditBot: string;
    RemoveBot: string;
    GetBotStates: string;

    StartBot: string;
    StartAllBots: string;
    StopBot: string;
    StopAllBots: string;
    SetCbrtRange: string;

    GetStableBalance: string;
    GetAssetBalance: string;
    AddStableBalance: string;
    AddAssetBalance: string;
    RemoveStableBalance: string;
    RemoveAssetBalance: string;
    MoveStableBalance: string;
    MoveAssetBalance: string;

    GetRawSubscription: string;
    AddSubscription: string;
    EditSubscription: string;
    RemoveSubscription: string;
    GetSubscriptionStates: string;
    ActiveAllSubscriptions: string;
    DeactivateAllSubscriptions: string;
  };
  ORDER: {
    Health: string;
    StartLogsWebsocket: string;
    StopLogsWebsocket: string;
    Websocket: string;

    StartOrder: string;
    StartSingleOrder: string;
  };
  EXCHANGE: {
    Health: string;
    StartLogsWebsocket: string;
    StopLogsWebsocket: string;
    Websocket: string;

    GetCandles: string;
    GetLatestPrice: string;
    GetLatestPrices: string;
    GetBalance: string;
    GetDecimals: string;
    GetOrderbook: string;
    CheckSupport: string;
    StartOrder: string;

    GetAvailableExchanges: string;
    GetAvailableExchangesByPairs: string;

    AddAPIKeys: string;
    RemoveAPIKeys: string;
  };
  PROFIT_CALCULATIONS: {
    Health: string;
    Websocket: string;
    GetSingleProfitCalculation: string;
    GetBestProfitCalculation: string;
    GetIndicators: string;
    GetLatestProfitCalculation: string;
    GetCalculationResultHistoryFiles: string;
    GetCalculationResult: string;
  };
};

const ServicePaths: ServicePaths = {
  [Services.Bot]: {
    Health: (port: number) => `${basePaths[Services.Bot]}:${port}/health`,
    StartLogsWebsocket: (port: number) =>
      `${basePaths[Services.Bot]}:${port}/websocket/start`,
    StopLogsWebsocket: (port: number) =>
      `${basePaths[Services.Bot]}:${port}/websocket/stop`,
    Websocket: (port: number) => `${websocketPaths[Services.Bot]}:${port + 1}`,

    GetBot: (port: number) => `${basePaths[Services.Bot]}:${port}/bot/get`,
    CreateComplexParams: (port: number) =>
      `${basePaths[Services.Bot]}:${port}/complex-params/new`,
    UpdateComplexParams: (port: number) =>
      `${basePaths[Services.Bot]}:${port}/complex-params/update`,
    PushComplexParams: (port: number) =>
      `${basePaths[Services.Bot]}:${port}/complex-params/push`,
  },
  [Services.Data]: {
    Health: `${basePaths[Services.Data]}/health`,
    StartLogsWebsocket: `${basePaths[Services.Data]}/websocket/start`,
    StopLogsWebsocket: `${basePaths[Services.Data]}/websocket/stop`,
    Websocket: websocketPaths[Services.Data],

    GetUser: `${basePaths[Services.Data]}/user/get`,
    AddUser: `${basePaths[Services.Data]}/user/add`,
    EditUser: `${basePaths[Services.Data]}/user/edit`,
    RemoveUser: `${basePaths[Services.Data]}/user/remove`,
    GetUserEnabledExchanges: `${basePaths[Services.Data]}/user/exchanges`,
    AddUserEnabledExchange: `${basePaths[Services.Data]}/user/exchange`,
    RemoveUserEnabledExchange: `${
      basePaths[Services.Data]
    }/user/exchange/remove`,
    UpdateUserTotalBalances: `${basePaths[Services.Data]}/user/updateBalances`,
    GetAllBotsByUserId: `${basePaths[Services.Data]}/user/bots`,
    GetClustersByUserId: `${basePaths[Services.Data]}/user/cluster/distinct`,

    GetBotIdsByClusterId: `${basePaths[Services.Data]}/cluster/bots/id`,

    AddBot: `${basePaths[Services.Data]}/bot/add`,
    EditBot: `${basePaths[Services.Data]}/bot/edit`,
    GetAllBots: `${basePaths[Services.Data]}/bots`,
    GetAllActiveBots: `${basePaths[Services.Data]}/bots/active`,
    IsPortAvailable: `${basePaths[Services.Data]}/isPortAvailable`,
    GetBot: `${basePaths[Services.Data]}/bot`,
    SetBotTimeFrames: `${basePaths[Services.Data]}/bot/timeFrame`,
    SetBotProcessID: `${basePaths[Services.Data]}/bot/processId`,
    SetBotComplexParams: `${basePaths[Services.Data]}/bot/complexParams`,
    SetBotIndicatorConfig: `${basePaths[Services.Data]}/bot/indicatorConfig`,
    SetDidEarlierOrder: `${
      basePaths[Services.Data]
    }/bot/didEarlierOrderTimestamp`,
    ResetRuntime: `${basePaths[Services.Data]}/bot/runtime/reset`,
    SetCbrtRange: `${basePaths[Services.Data]}/bot/cbrtRange`,
    SetLatestOrder: `${basePaths[Services.Data]}/bot/latestOrder`,
    ResetCbrtRange: `${basePaths[Services.Data]}/bot/cbrtRange/reset`,
    DisableBot: `${basePaths[Services.Data]}/bot/disable`,
    StartBot: `${basePaths[Services.Data]}/bot/start`,
    StopBot: `${basePaths[Services.Data]}/bot/stop`,
    SetRetryUpdateComplexParams: `${
      basePaths[Services.Data]
    }/bot/retryUpdateComplexParams`,
    SetLastUpdated: `${basePaths[Services.Data]}/bot/lastUpdated`,
    SetNextWakeUpState: `${basePaths[Services.Data]}/bot/nextWakeUpState`,
    SetActivePort: `${basePaths[Services.Data]}/bot/activePort`,
    SetProfitCalculationsStartTimestamp: `${
      basePaths[Services.Data]
    }/bot/profitCalculationsStartTimestamp`,
    GetBotByBaseAsset: `${basePaths[Services.Data]}/bot/baseAsset`,
    UpdateBalance: `${basePaths[Services.Data]}/bot/balance/update`,
    RemoveBot: `${basePaths[Services.Data]}/bot/remove`,
    GetBotAvailableExchangesByBotFriendlyName: `${
      basePaths[Services.Data]
    }/bot/exchanges`,
    SetBotDefaultExchange: `${basePaths[Services.Data]}/bot/exchange`,
    AddBotAvailableExchanges: `${basePaths[Services.Data]}/bot/exchange/add`,
    RemoveBotAvailableExchanges: `${
      basePaths[Services.Data]
    }/bot/exchange/remove`,
    ResetBotAvailableExchanges: `${
      basePaths[Services.Data]
    }/bot/exchanges/reset`,
    ResetAllBotsAvailableExchanges: `${
      basePaths[Services.Data]
    }/bot/exchanges/reset/all`,
    ResetMultiOrder: `${basePaths[Services.Data]}/bot/multiOrder/reset`,
    FetchSubscriptionsByBotId: `${basePaths[Services.Data]}/sub/bot`,
    SetMultiOrder: `${basePaths[Services.Data]}/bot/multiOrder`,
    SetLatestTransactionType: `${
      basePaths[Services.Data]
    }/bot/latestTransactionType`,

    AddSubscription: `${basePaths[Services.Data]}/sub`,
    RemoveSubscription: `${basePaths[Services.Data]}/sub/remove`,
    GetSubscriptionByUserIdAndBotIdUrlClusterId: `${
      basePaths[Services.Data]
    }/sub/user/bot`,
    SubscriptionBalance: `${basePaths[Services.Data]}/sub/balance`,
    GetSubscriptionsByUserId: `${basePaths[Services.Data]}/sub/user`,
    SubscriptionExchange: `${basePaths[Services.Data]}/sub/exchange`,
    SetSubscriptionRuntimeVolume: `${
      basePaths[Services.Data]
    }/sub/runtime/volume`,
    ResetSubscriptionRuntimeVolume: `${
      basePaths[Services.Data]
    }/sub/runtime/volume/reset`,

    AddTransaction: `${basePaths[Services.Data]}/transaction`,
    AddCustomTransaction: `${basePaths[Services.Data]}/transaction/custom`,
    GetTransactionSize: `${basePaths[Services.Data]}/transaction/size`,
    GetCustomTransactionSize: `${
      basePaths[Services.Data]
    }/transaction/custom/size`,

    GetClientHealthCheck: `${basePaths[Services.Data]}/client/health`,
    GetClientOverview: `${basePaths[Services.Data]}/client/overview`,
    GetClientSubscription: `${basePaths[Services.Data]}/client/sub`,
    GetClientNextWakeUpInfo: `${
      basePaths[Services.Data]
    }/client/bot/nextWakeUp`,
    FetchClientNextOrderValue: `${
      basePaths[Services.Data]
    }/client/bot/nextOrder`,
    GetClientBotProfitCharts: `${
      basePaths[Services.Data]
    }/client/sub/profit/chart`,
    GetClientBotProfits: `${basePaths[Services.Data]}/client/sub/profit`,
    GetClientBotProfitsByPeriod: `${
      basePaths[Services.Data]
    }/client/sub/profit/period`,
    GetClientBotTransactions: `${
      basePaths[Services.Data]
    }/client/sub/transactions`,
    GetClientLatestTransaction: `${
      basePaths[Services.Data]
    }/client/sub/transaction/latest`,
    GetClientBotWakeups: `${basePaths[Services.Data]}/client/bot/wakeups`,
    GetClientLatestBotState: `${
      basePaths[Services.Data]
    }/client/bot/latestState`,

    ReorderDaemonQueue: `${
      basePaths[Services.Data]
    }/calculations/daemon/queue/reorder`,
  },
  [Services.CandleFetcher]: {
    Health: `${basePaths[Services.CandleFetcher]}/health`,
    StartLogsWebsocket: `${basePaths[Services.CandleFetcher]}/websocket/start`,
    StopLogsWebsocket: `${basePaths[Services.CandleFetcher]}/websocket/stop`,
    Websocket: websocketPaths[Services.CandleFetcher],

    CheckCompleteStorage: `${
      basePaths[Services.CandleFetcher]
    }/storage/complete`,
    FetchCandles: `${basePaths[Services.CandleFetcher]}/fetch/candles`,
    FetchHeikinCandles: `${
      basePaths[Services.CandleFetcher]
    }/fetch/candles/heikin`,
    FetchPreviousHeikinCandle: `${
      basePaths[Services.CandleFetcher]
    }/fetch/heikin/previous`,
    FetchLatestHeikinCandle: `${
      basePaths[Services.CandleFetcher]
    }/fetch/heikin/latest`,
    FetchCurrentHeikinCandle: `${
      basePaths[Services.CandleFetcher]
    }/fetch/heikin/current`,
    FetchLatestNormalCandle: `${
      basePaths[Services.CandleFetcher]
    }/fetch/normal/latest`,
    FetchCurrentNormalCandle: `${
      basePaths[Services.CandleFetcher]
    }/fetch/normal/current`,
    StartFetchInterval: `${basePaths[Services.CandleFetcher]}/fetch/start`,
    StopFetchInterval: `${basePaths[Services.CandleFetcher]}/fetch/stop`,
    SetCandleDecision: `${basePaths[Services.CandleFetcher]}/decision`,
  },
  [Services.Control]: {
    Health: `${basePaths[Services.Control]}/health`,
    StartLogsWebsocket: `${basePaths[Services.Control]}/websocket/start`,
    StopLogsWebsocket: `${basePaths[Services.Control]}/websocket/stop`,
    Websocket: websocketPaths[Services.Control],

    GetUser: `${basePaths[Services.Control]}/user/get`,
    EditUser: `${basePaths[Services.Control]}/user/edit`,
    AddUser: `${basePaths[Services.Control]}/user/add`,
    RemoveUser: `${basePaths[Services.Control]}/user/remove`,
    HasPassword: `${basePaths[Services.Control]}/user/hasPassword`,
    SetPassword: `${basePaths[Services.Control]}/user/password`,
    RemovePassword: `${basePaths[Services.Control]}/user/password/remove`,
    PanicSell: `${basePaths[Services.Control]}/user/panic/sell`,

    GetRawBot: `${basePaths[Services.Control]}/bot/get`,
    AddBot: `${basePaths[Services.Control]}/bot/add`,
    EditBot: `${basePaths[Services.Control]}/bot/edit`,
    RemoveBot: `${basePaths[Services.Control]}/bot/remove`,
    GetBotStates: `${basePaths[Services.Control]}/bot/states`,

    StartBot: `${basePaths[Services.Control]}/bot/start`,
    StartAllBots: `${basePaths[Services.Control]}/bot/start/all`,
    StopBot: `${basePaths[Services.Control]}/bot/stop`,
    StopAllBots: `${basePaths[Services.Control]}/bot/stop/all`,
    SetCbrtRange: `${basePaths[Services.Control]}/bot/cbrtRange/set`,

    GetStableBalance: `${basePaths[Services.Control]}/balance/sub/stable`,
    GetAssetBalance: `${basePaths[Services.Control]}/balance/sub/asset`,
    AddStableBalance: `${basePaths[Services.Control]}/balance/sub/stable/add`,
    AddAssetBalance: `${basePaths[Services.Control]}/balance/sub/asset/add`,
    RemoveStableBalance: `${
      basePaths[Services.Control]
    }/balance/sub/stable/remove`,
    RemoveAssetBalance: `${
      basePaths[Services.Control]
    }/balance/sub/asset/remove`,
    MoveStableBalance: `${basePaths[Services.Control]}/balance/sub/stable/move`,
    MoveAssetBalance: `${basePaths[Services.Control]}/balance/sub/asset/move`,

    GetRawSubscription: `${basePaths[Services.Control]}/sub`,
    AddSubscription: `${basePaths[Services.Control]}/sub/add`,
    EditSubscription: `${basePaths[Services.Control]}/sub/edit`,
    RemoveSubscription: `${basePaths[Services.Control]}/sub/remove`,
    GetSubscriptionStates: `${basePaths[Services.Control]}/sub/states`,
    ActiveAllSubscriptions: `${basePaths[Services.Control]}/sub/activate/all`,
    DeactivateAllSubscriptions: `${
      basePaths[Services.Control]
    }/sub/deactivate/all`,
  },
  [Services.Order]: {
    Health: `${basePaths[Services.Order]}/health`,
    StartLogsWebsocket: `${basePaths[Services.Order]}/websocket/start`,
    StopLogsWebsocket: `${basePaths[Services.Order]}/websocket/stop`,
    Websocket: websocketPaths[Services.Order],

    StartOrder: `${basePaths[Services.Order]}/order`,
    StartSingleOrder: `${basePaths[Services.Order]}/order/single`,
  },
  [Services.Exchange]: {
    Health: `${basePaths[Services.Exchange]}/health`,
    StartLogsWebsocket: `${basePaths[Services.Exchange]}/websocket/start`,
    StopLogsWebsocket: `${basePaths[Services.Exchange]}/websocket/stop`,
    Websocket: websocketPaths[Services.Exchange],

    GetCandles: `${basePaths[Services.Exchange]}/exchange/candles`,
    GetLatestPrice: `${basePaths[Services.Exchange]}/exchange/price`,
    GetLatestPrices: `${basePaths[Services.Exchange]}/exchange/prices`,
    GetBalance: `${basePaths[Services.Exchange]}/exchange/balance`,
    GetDecimals: `${basePaths[Services.Exchange]}/exchange/decimals`,
    GetOrderbook: `${basePaths[Services.Exchange]}/exchange/orderbook`,
    CheckSupport: `${basePaths[Services.Exchange]}/exchange/support`,
    StartOrder: `${basePaths[Services.Exchange]}/exchange/order`,

    GetAvailableExchanges: `${
      basePaths[Services.Exchange]
    }/exchange/availables`,
    GetAvailableExchangesByPairs: `${
      basePaths[Services.Exchange]
    }/exchange/availables/all`,

    AddAPIKeys: `${basePaths[Services.Exchange]}/keys`,
    RemoveAPIKeys: `${basePaths[Services.Exchange]}/keys/remove`,
  },
  [Services.ProfitCalculations]: {
    Health: `${basePaths[Services.ProfitCalculations]}/health`,
    Websocket: websocketPaths[Services.ProfitCalculations],
    // StartLogsWebsocket: `${basePaths[Services.ProfitCalculations]}/websocket/start`,
    // StopLogsWebsocket: `${basePaths[Services.ProfitCalculations]}/websocket/stop`,

    GetSingleProfitCalculation: `${
      basePaths[Services.ProfitCalculations]
    }/profit/hypothetical`,
    GetBestProfitCalculation: `${
      basePaths[Services.ProfitCalculations]
    }/profit/hypothetical/best`,
    GetIndicators: `${basePaths[Services.ProfitCalculations]}/indicators`,
    GetLatestProfitCalculation: `${
      basePaths[Services.ProfitCalculations]
    }/statistics/latest`,
    GetCalculationResultHistoryFiles: `${
      basePaths[Services.ProfitCalculations]
    }/statistics/files`,
    GetCalculationResult: `${
      basePaths[Services.ProfitCalculations]
    }/statistics/result`,
  },
};

export default ServicePaths;
